import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchDashboard } from '../api/endpoints';
import { queryKeys } from '../api/query-keys';
import type { Cooldown, DashboardResponse, DecisionRecord, Indicators } from '../api/schemas';
import { Banner } from '../components/Banner';
import { KeyValue } from '../components/KeyValue';
import { Percent, Price, Usdt } from '../components/Money';
import { NoData } from '../components/NoData';
import { PositionCard } from '../components/PositionCard';
import { QueryState } from '../components/QueryState';
import { ReasonText } from '../components/ReasonText';
import { Section } from '../components/Section';
import { StatusPill } from '../components/StatusPill';
import {
  CONNECTION_STATUS_LABEL,
  CONNECTION_STATUS_TONE,
  DECISION_LABEL,
  DECISION_TONE,
  INDICATOR_MODE_LABEL,
  STRATEGY_STATE_HINT,
  STRATEGY_STATE_LABEL,
  STRATEGY_STATE_TONE,
  positionStateRu,
  WARNING_LEVEL_LABEL,
  WARNING_LEVEL_TONE,
} from '../domain/labels';
import { formatDateOnly, formatDateTime, formatRelative } from '../lib/datetime';

/**
 * Раздел 1 — «Обзор».
 *
 * Everything an operator needs in order to decide whether to intervene, on one
 * screen, without a single navigation. Nothing here is actionable: commands
 * live in «Управление», so a glance at the state cannot turn into an accidental
 * order.
 */
export function OverviewPage(): ReactElement {
  const query = useQuery({
    queryKey: queryKeys.dashboard,
    queryFn: ({ signal }) => fetchDashboard(signal),
  });

  return (
    <>
      <div className="page-head">
        <h1>Обзор</h1>
        <span className="page-head__hint">Текущее состояние бота, рынка и позиции.</span>
      </div>
      <QueryState query={query} what="панель обзора">
        {(data) => <OverviewContent data={data} />}
      </QueryState>
    </>
  );
}

function OverviewContent({ data }: { data: DashboardResponse }): ReactElement {
  return (
    <div className="stack">
      <div className="grid">
        <Section title="Состояние стратегии">
          <div className="stack stack--tight">
            <StatusPill tone={STRATEGY_STATE_TONE[data.strategyState]} srPrefix="Стратегия">
              {STRATEGY_STATE_LABEL[data.strategyState]}
            </StatusPill>
            <p className="muted" style={{ fontSize: '0.85rem' }}>
              {STRATEGY_STATE_HINT[data.strategyState]}
            </p>
            <KeyValue
              rows={[
                { label: 'Инструмент', value: data.symbol },
                {
                  label: 'Следующий запуск',
                  value:
                    data.nextRun.scheduledAt === null ? (
                      <span className="muted">не запланирован</span>
                    ) : (
                      <span>
                        {formatDateTime(data.nextRun.scheduledAt)}{' '}
                        <span className="muted">({formatRelative(data.nextRun.scheduledAt)})</span>
                      </span>
                    ),
                },
                { label: 'Таймзона стратегии', value: data.nextRun.timezone },
              ]}
            />
          </div>
        </Section>

        <Section title="Подключения" hint="Состояние внешних каналов на текущий момент.">
          <KeyValue
            rows={[
              {
                label: 'Рыночные данные',
                value: (
                  <StatusPill
                    tone={CONNECTION_STATUS_TONE[data.connections.marketData]}
                    srPrefix="Рыночные данные"
                  >
                    {CONNECTION_STATUS_LABEL[data.connections.marketData]}
                  </StatusPill>
                ),
              },
              {
                label: 'Торговая площадка',
                value: (
                  <StatusPill
                    tone={CONNECTION_STATUS_TONE[data.connections.executionVenue]}
                    srPrefix="Торговая площадка"
                  >
                    {CONNECTION_STATUS_LABEL[data.connections.executionVenue]}
                  </StatusPill>
                ),
              },
              {
                label: 'Telegram',
                value: (
                  <StatusPill
                    tone={CONNECTION_STATUS_TONE[data.connections.telegram]}
                    srPrefix="Telegram"
                  >
                    {CONNECTION_STATUS_LABEL[data.connections.telegram]}
                  </StatusPill>
                ),
              },
            ]}
          />
        </Section>

        <Section title="Рынок и счёт">
          <MarketAndAccount price={data.price} balance={data.balance} />
        </Section>
      </div>

      <Section title="Открытая позиция">
        <PositionCard position={data.position} />
      </Section>

      <div className="grid grid--wide">
        <Section title="Индикаторы">
          <IndicatorsBlock indicators={data.indicators} />
        </Section>

        <Section title="Импульсная пауза">
          <CooldownBlock cooldown={data.cooldown} />
        </Section>
      </div>

      <Section title="Последнее решение">
        <LastDecisionBlock decision={data.lastDecision} />
      </Section>

      <Section title="Последние предупреждения">
        {data.recentWarnings.length === 0 ? (
          <p className="empty-state">Предупреждений нет.</p>
        ) : (
          <ul className="stack stack--tight">
            {data.recentWarnings.map((warning) => (
              <li key={warning.id} className="row">
                <StatusPill
                  tone={WARNING_LEVEL_TONE[warning.level]}
                  srPrefix="Уровень предупреждения"
                >
                  {WARNING_LEVEL_LABEL[warning.level]}
                </StatusPill>
                <span className="reason-text">{warning.messageRu}</span>
                <span className="muted" style={{ fontSize: '0.78rem' }}>
                  {formatDateTime(warning.at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

/**
 * Price and balance are nullable, and both go blank the moment their feed
 * drops. They are rendered by one component so the two cannot drift into
 * different ways of saying "unknown" — and so the missing-feed banners sit
 * immediately above the empty rows they explain, rather than only in the
 * warnings list at the bottom of the page.
 */
function MarketAndAccount({
  price,
  balance,
}: {
  price: DashboardResponse['price'];
  balance: DashboardResponse['balance'];
}): ReactElement {
  return (
    <div className="stack stack--tight">
      {price === null && (
        <Banner tone="danger" title="Рыночные данные недоступны.">
          Текущая цена не получена. Ни одно значение ниже не является актуальной ценой.
        </Banner>
      )}
      {balance === null && (
        <Banner tone="warn" title="Баланс счёта недоступен.">
          Подключение к торговой площадке не установлено, остатки по счёту не прочитаны.
        </Banner>
      )}
      <KeyValue
        rows={[
          {
            label: 'Текущая цена',
            value:
              price === null ? (
                <NoData reasonRu="рыночные данные недоступны" />
              ) : (
                <span>
                  <Price value={price.value} />{' '}
                  <span className="muted">({formatRelative(price.asOf)})</span>
                </span>
              ),
          },
          {
            label: 'Данные на',
            value:
              price === null ? (
                <NoData reasonRu="рыночные данные недоступны" />
              ) : (
                formatDateTime(price.asOf)
              ),
          },
          {
            label: 'Баланс',
            value:
              balance === null ? (
                <NoData reasonRu="счёт недоступен" />
              ) : (
                <Usdt value={balance.total} />
              ),
          },
          {
            label: 'Доступно',
            value:
              balance === null ? (
                <NoData reasonRu="счёт недоступен" />
              ) : (
                <Usdt value={balance.available} />
              ),
          },
          {
            label: 'Зарезервировано',
            value:
              balance === null ? (
                <NoData reasonRu="счёт недоступен" />
              ) : (
                <Usdt value={balance.reserved} />
              ),
          },
        ]}
      />
    </div>
  );
}

function IndicatorsBlock({ indicators }: { indicators: Indicators }): ReactElement {
  return (
    <KeyValue
      rows={[
        { label: 'Режим расчёта', value: INDICATOR_MODE_LABEL[indicators.mode] },
        { label: 'Таймфрейм', value: indicators.timeframe },
        {
          label: 'Закрытие свечи',
          value:
            indicators.candleCloseTime === null ? (
              <NoData reasonRu="закрытых свечей ещё не получено" />
            ) : (
              formatDateTime(indicators.candleCloseTime)
            ),
        },
        {
          label: 'RSI',
          value:
            indicators.rsi === null ? (
              <span className="muted">нет данных</span>
            ) : (
              <span className="num">{indicators.rsi}</span>
            ),
        },
        {
          label: 'Сглаженный RSI',
          value:
            indicators.smoothedRsi === null ? (
              <span className="muted">нет данных</span>
            ) : (
              <span className="num">{indicators.smoothedRsi}</span>
            ),
        },
        {
          label: 'Предварительный сигнал',
          value:
            indicators.preliminarySignal === null ? (
              <span className="muted">не определён</span>
            ) : (
              <StatusPill
                tone={DECISION_TONE[indicators.preliminarySignal]}
                srPrefix="Предварительный сигнал"
              >
                {DECISION_LABEL[indicators.preliminarySignal]}
              </StatusPill>
            ),
        },
      ]}
    />
  );
}

function CooldownBlock({ cooldown }: { cooldown: Cooldown }): ReactElement {
  if (!cooldown.active) {
    return (
      <div className="stack stack--tight">
        <StatusPill tone="ok" srPrefix="Импульсная пауза">
          Пауза не активна
        </StatusPill>
        <p className="muted" style={{ fontSize: '0.85rem' }}>
          Импульсного роста, требующего пропуска входов, не зафиксировано.
        </p>
      </div>
    );
  }

  return (
    <div className="stack stack--tight">
      <StatusPill tone="warn" srPrefix="Импульсная пауза">
        Пауза активна
      </StatusPill>
      <KeyValue
        rows={[
          {
            label: 'Причина',
            value: cooldown.reasonRu ?? <span className="muted">не указана</span>,
          },
          {
            label: 'Осталось пропусков',
            value:
              cooldown.remainingSkips === null ? (
                <span className="muted">—</span>
              ) : (
                <span className="num">{cooldown.remainingSkips}</span>
              ),
          },
          {
            label: 'Ближайший разрешённый день',
            value: formatDateOnly(cooldown.nextEligibleDate),
          },
          ...(cooldown.impulse === null
            ? []
            : [
                {
                  label: 'Импульс зафиксирован',
                  value: formatDateTime(cooldown.impulse.detectedAt),
                },
                {
                  label: 'Величина импульса',
                  value: <Percent value={cooldown.impulse.valuePct} />,
                },
                {
                  label: 'Открытие дня',
                  value: <Price value={cooldown.impulse.dayOpen} />,
                },
                {
                  label: 'Максимум дня',
                  value: <Price value={cooldown.impulse.knownDayHigh} />,
                },
              ]),
        ]}
      />
    </div>
  );
}

function LastDecisionBlock({ decision }: { decision: DecisionRecord | null }): ReactElement {
  if (decision === null) {
    return <p className="empty-state">Решений пока не было.</p>;
  }

  return (
    <div className="stack stack--tight">
      <div className="row">
        <StatusPill tone={DECISION_TONE[decision.decision]} srPrefix="Решение">
          {DECISION_LABEL[decision.decision]}
        </StatusPill>
        <span className="muted" style={{ fontSize: '0.8rem' }}>
          {formatDateTime(decision.evaluatedAt)}
        </span>
      </div>
      <Banner tone="info" title="Причина решения:">
        <ReasonText code={decision.reasonCode} />
      </Banner>
      <KeyValue
        rows={[
          { label: 'Торговая дата', value: formatDateOnly(decision.localTradingDate) },
          { label: 'План на', value: formatDateTime(decision.scheduledAt) },
          { label: 'Данные на', value: formatDateTime(decision.marketDataAsOf) },
          { label: 'Таймфрейм', value: decision.timeframe },
          { label: 'RSI', value: decision.rsi ?? <NoData /> },
          { label: 'Сглаженный RSI', value: decision.smoothedRsi ?? <NoData /> },
          { label: 'Состояние позиции', value: positionStateRu(decision.positionState) },
          { label: 'Версия стратегии', value: decision.strategyVersion },
        ]}
      />
    </div>
  );
}
